/* eslint-disable */

/**
 * Campaigns Vuex Store Module
 * 
 * Manages state for the Campaigns feature including:
 * - Campaign list
 * - Campaign analytics
 * - Recipient lists
 * - Campaign templates
 */

import axios from 'axios';
import { extractApiError } from '@/utils/apiError';

const state = {
  campaigns: [],
  archivedCampaigns: [],
  currentCampaign: null,
  campaignStats: {
    totalCampaigns: 0,
    messagesSent: 0,
    totalRecipients: 0,
    avgDeliveryRate: 0
  },
  recipients: [],
  patientLists: [],
  smsTemplates: [],
  emailTemplates: [],

  // Email domain statistics for analytics
  campaignDomainStats: [],

  // Channel-aware site-level stats — the real getSiteStatistics payload
  // ({ overall, byChannel, byCampaign, byDomain }); customer-facing units only.
  siteStats: null,

  // A/B Tests
  abTests: [],
  currentAbTest: null,

  // Campaign templates
  campaignTemplates: [],

  // Global statistics
  globalStats: {
    deliveryRate: 0,
    openRate: 0,
    clickRate: 0,
    ctor: 0,
    bounceRate: 0,
    spamComplaintRate: 0,
    unsubscribeRate: 0,
    totalDelivered: 0,
    totalOpened: 0,
    totalClicked: 0,
    totalBounced: 0,
    totalComplaints: 0,
    totalUnsubscribed: 0
  },

  // Timeline data for charts
  statsTimeline: [],

  // Polling for campaign send progress
  pollingInterval: null,

  loading: false,
  error: null
};

// Mock data for email domain statistics
const mockDomainStats = [
  { domain: 'gmail.com', count: 4521, percentage: 45.2 },
  { domain: 'yahoo.com', count: 1823, percentage: 18.2 },
  { domain: 'outlook.com', count: 1245, percentage: 12.5 },
  { domain: 'hotmail.com', count: 892, percentage: 8.9 },
  { domain: 'aol.com', count: 456, percentage: 4.6 },
  { domain: 'icloud.com', count: 389, percentage: 3.9 },
  { domain: 'Other', count: 674, percentage: 6.7 }
];

// Mock data for A/B tests
const mockAbTests = [
  {
    AbTestID: '1',
    Name: 'Subject Line Test - Recall Campaign',
    Status: 'Running',
    Type: 'SubjectLine',
    CreatedAt: '2024-03-15T10:00:00Z',
    VariantA: {
      subject: 'Time for your dental checkup!',
      sent: 500,
      opened: 234,
      clicked: 45,
      openRate: 46.8,
      clickRate: 9.0
    },
    VariantB: {
      subject: '{{FirstName}}, your smile needs attention',
      sent: 500,
      opened: 289,
      clicked: 67,
      openRate: 57.8,
      clickRate: 13.4
    },
    Winner: null,
    WinnerCriteria: 'OpenRate',
    TestDuration: '24h'
  }
];

// Mock data for campaign templates
const mockCampaignTemplates = [
  {
    TemplateID: '1',
    Name: 'Appointment Reminder',
    Type: 'Email',
    Subject: 'Your appointment is coming up!',
    Content: '<h1>Appointment Reminder</h1><p>Dear {{FirstName}},</p><p>This is a reminder...</p>',
    CreatedAt: '2024-01-10T08:00:00Z',
    UpdatedAt: '2024-03-01T14:30:00Z',
    UsageCount: 45
  },
  {
    TemplateID: '2',
    Name: 'Welcome New Patient',
    Type: 'Email',
    Subject: 'Welcome to {{PracticeName}}!',
    Content: '<h1>Welcome!</h1><p>Dear {{FirstName}},</p><p>We are excited to have you...</p>',
    CreatedAt: '2024-01-15T09:00:00Z',
    UpdatedAt: '2024-02-20T11:00:00Z',
    UsageCount: 128
  },
  {
    TemplateID: '3',
    Name: 'Recall Notification',
    Type: 'Both',
    Subject: 'Time for your checkup, {{FirstName}}!',
    SmsContent: 'Hi {{FirstName}}! It\'s time for your dental checkup at {{PracticeName}}. Call us to schedule!',
    Content: '<h1>Time for a Checkup</h1><p>Dear {{FirstName}},</p><p>It has been 6 months...</p>',
    CreatedAt: '2024-02-01T10:00:00Z',
    UpdatedAt: '2024-03-10T16:00:00Z',
    UsageCount: 89
  }
];

// Mock global statistics
const mockGlobalStats = {
  deliveryRate: 97.2,
  openRate: 42.5,
  clickRate: 12.8,
  ctor: 30.1,
  bounceRate: 2.8,
  spamComplaintRate: 0.1,
  unsubscribeRate: 0.3,
  totalDelivered: 15420,
  totalOpened: 6554,
  totalClicked: 1974,
  totalBounced: 432,
  totalComplaints: 15,
  totalUnsubscribed: 46
};

// Mock timeline data for charts
const mockStatsTimeline = [
  { date: '2024-03-01', delivered: 1200, opened: 510, clicked: 153, bounced: 34 },
  { date: '2024-03-02', delivered: 980, opened: 417, clicked: 125, bounced: 28 },
  { date: '2024-03-03', delivered: 1450, opened: 617, clicked: 185, bounced: 41 },
  { date: '2024-03-04', delivered: 1100, opened: 468, clicked: 140, bounced: 31 },
  { date: '2024-03-05', delivered: 1320, opened: 561, clicked: 168, bounced: 37 },
  { date: '2024-03-06', delivered: 890, opened: 378, clicked: 114, bounced: 25 },
  { date: '2024-03-07', delivered: 1560, opened: 663, clicked: 199, bounced: 44 }
];

const getters = {
  allCampaigns: (state) => state.campaigns,
  archivedCampaigns: (state) => state.archivedCampaigns,
  currentCampaign: (state) => state.currentCampaign,
  campaignStats: (state) => state.campaignStats,
  recipients: (state) => state.recipients,
  patientLists: (state) => state.patientLists,
  smsTemplates: (state) => state.smsTemplates,
  emailTemplates: (state) => state.emailTemplates,
  isLoading: (state) => state.loading,
  error: (state) => state.error,

  // New getters for enhanced features
  campaignDomainStats: (state) => state.campaignDomainStats,
  abTests: (state) => state.abTests,
  currentAbTest: (state) => state.currentAbTest,
  campaignTemplates: (state) => state.campaignTemplates,
  globalStats: (state) => state.globalStats,
  statsTimeline: (state) => state.statsTimeline,

  // Filtered campaigns
  draftCampaigns: (state) => state.campaigns.filter(c => c.Status === 'Draft'),
  activeCampaigns: (state) => state.campaigns.filter(c => c.Status === 'Active' || c.Status === 'Scheduled'),
  completedCampaigns: (state) => state.campaigns.filter(c => c.Status === 'Completed'),

  // Campaign by ID
  getCampaignById: (state) => (id) => state.campaigns.find(c => c.CampaignID === id),

  // A/B Test by ID
  getAbTestById: (state) => (id) => state.abTests.find(t => t.AbTestID === id),

  // Template by ID
  getTemplateById: (state) => (id) => state.campaignTemplates.find(t => t.TemplateID === id)
};

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  
  SET_ERROR(state, error) {
    state.error = error;
  },
  
  SET_CAMPAIGNS(state, campaigns) {
    state.campaigns = campaigns;
  },
  
  ADD_CAMPAIGN(state, campaign) {
    state.campaigns.unshift(campaign);
  },
  
  UPDATE_CAMPAIGN(state, updatedCampaign) {
    const index = state.campaigns.findIndex(c => c.CampaignID === updatedCampaign.CampaignID);
    if (index !== -1) {
      state.campaigns.splice(index, 1, updatedCampaign);
    }
  },
  
  DELETE_CAMPAIGN(state, campaignId) {
    state.campaigns = state.campaigns.filter(c => c.CampaignID !== campaignId && c.CampaignIDForWeb !== campaignId);
  },
  
  SET_CURRENT_CAMPAIGN(state, campaign) {
    state.currentCampaign = campaign;
  },
  
  SET_CAMPAIGN_STATS(state, stats) {
    state.campaignStats = stats;
  },

  SET_SITE_STATS(state, data) {
    state.siteStats = data;
  },

  SET_RECIPIENTS(state, recipients) {
    state.recipients = recipients;
  },
  
  SET_PATIENT_LISTS(state, lists) {
    state.patientLists = lists;
  },
  
  SET_SMS_TEMPLATES(state, templates) {
    state.smsTemplates = templates;
  },
  
  SET_EMAIL_TEMPLATES(state, templates) {
    state.emailTemplates = templates;
  },

  // New mutations for enhanced features
  SET_ARCHIVED_CAMPAIGNS(state, campaigns) {
    state.archivedCampaigns = campaigns;
  },

  ARCHIVE_CAMPAIGN(state, campaignId) {
    const index = state.campaigns.findIndex(c => c.CampaignID === campaignId);
    if (index !== -1) {
      const campaign = { ...state.campaigns[index], Status: 'Archived' };
      state.campaigns.splice(index, 1);
      state.archivedCampaigns.unshift(campaign);
    }
  },

  RESTORE_CAMPAIGN(state, campaignId) {
    const index = state.archivedCampaigns.findIndex(c => c.CampaignID === campaignId);
    if (index !== -1) {
      const campaign = { ...state.archivedCampaigns[index], Status: 'Draft' };
      state.archivedCampaigns.splice(index, 1);
      state.campaigns.unshift(campaign);
    }
  },

  SET_CAMPAIGN_DOMAIN_STATS(state, stats) {
    state.campaignDomainStats = stats;
  },

  SET_AB_TESTS(state, tests) {
    state.abTests = tests;
  },

  ADD_AB_TEST(state, test) {
    state.abTests.unshift(test);
  },

  UPDATE_AB_TEST(state, updatedTest) {
    const index = state.abTests.findIndex(t => t.AbTestID === updatedTest.AbTestID);
    if (index !== -1) {
      state.abTests.splice(index, 1, updatedTest);
    }
  },

  DELETE_AB_TEST(state, testId) {
    state.abTests = state.abTests.filter(t => t.AbTestID !== testId);
  },

  SET_CURRENT_AB_TEST(state, test) {
    state.currentAbTest = test;
  },

  SET_CAMPAIGN_TEMPLATES(state, templates) {
    state.campaignTemplates = templates;
  },

  ADD_CAMPAIGN_TEMPLATE(state, template) {
    state.campaignTemplates.unshift(template);
  },

  UPDATE_CAMPAIGN_TEMPLATE(state, updatedTemplate) {
    const index = state.campaignTemplates.findIndex(t => t.TemplateID === updatedTemplate.TemplateID);
    if (index !== -1) {
      state.campaignTemplates.splice(index, 1, updatedTemplate);
    }
  },

  DELETE_CAMPAIGN_TEMPLATE(state, templateId) {
    state.campaignTemplates = state.campaignTemplates.filter(t => t.TemplateID !== templateId);
  },

  SET_POLLING_INTERVAL(state, interval) {
    state.pollingInterval = interval;
  },

  SET_GLOBAL_STATS(state, stats) {
    state.globalStats = stats;
  },

  SET_STATS_TIMELINE(state, timeline) {
    state.statsTimeline = timeline;
  }
};

const actions = {
  /**
   * Fetch all campaigns for a site
   */
  async fetchCampaigns({ commit, rootState }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns`, {
        params: { siteID }
      });
      
      commit('SET_CAMPAIGNS', response.data.campaigns || []);
      commit('SET_CAMPAIGN_STATS', response.data.stats || state.campaignStats);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch campaigns');
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Fetch channel-aware site-level statistics for the Reports page
   * (GET /campaign-statistics = the getSiteStatistics endpoint).
   * Returns { overall, byChannel, byCampaign, byDomain } — customer-facing units only.
   */
  async fetchSiteStats({ commit, rootState }, { startDate, endDate } = {}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const siteID = rootState.customer.siteID;
      const params = { siteID };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-statistics`, { params });
      commit('SET_SITE_STATS', response.data || null);
      return response.data;
    } catch (error) {
      console.error('Error fetching site statistics:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch statistics');
      return null;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch a single campaign by ID
   */
  async fetchCampaignById({ commit, rootState }, campaignId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}`, {
        params: { siteID }
      });
      
      commit('SET_CURRENT_CAMPAIGN', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch campaign');
      return null;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch campaign statistics
   */
  async fetchCampaignStatistics({ commit, rootState }, campaignId) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-statistics/${campaignId}`, {
        params: { siteID }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign statistics:', error);
      return null;
    }
  },
  
  /**
   * Create a new campaign
   */
  async createCampaign({ commit, rootState }, campaignData) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const siteID = rootState.customer.siteID;
      const user = rootState.customer.user;
      
      const payload = {
        ...campaignData,
        siteID,
        CreatedBy: user?.Name || 'Unknown',
        CreatedAt: new Date().toISOString()
      };
      
      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns`, payload);
      
      const newCampaign = response.data.campaign || response.data;
      commit('ADD_CAMPAIGN', newCampaign);
      return newCampaign;
    } catch (error) {
      console.error('Error creating campaign:', error);
      commit('SET_ERROR', error.message || 'Failed to create campaign');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Update an existing campaign
   */
  async updateCampaign({ commit, rootState }, { campaignId, campaignData }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const siteID = rootState.customer.siteID;
      
      const response = await axios.put(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}`, {
        ...campaignData,
        siteID
      });
      
      const updatedCampaign = response.data.campaign || response.data;
      commit('UPDATE_CAMPAIGN', updatedCampaign);
      return updatedCampaign;
    } catch (error) {
      console.error('Error updating campaign:', error);
      commit('SET_ERROR', error.message || 'Failed to update campaign');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Delete a campaign
   */
  async deleteCampaign({ commit, rootState }, campaignId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const siteID = rootState.customer.siteID;
      
      await axios.delete(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}`, {
        params: { siteID }
      });
      
      commit('DELETE_CAMPAIGN', campaignId);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      commit('SET_ERROR', error.message || 'Failed to delete campaign');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Send a campaign (start sending messages asynchronously)
   * Returns immediately — use startCampaignPolling to track progress
   *
   * @param {string} campaignId - CampaignIDForWeb
   * @param {string} campaignType - 'SMS', 'Email', or 'Both'
   * @param {Object} [customFilters] - Custom filter config from CustomFilterBuilder
   * @param {Array} [selectedList] - Array of PatNum values
   * @param {string} [smsContent] - SMS message content
   * @param {number} [callFollowupTaskTypeID] - For ai_call campaigns: 3=Overdue Recall, 4=Unscheduled Treatment
   */
  async sendCampaign({ commit, dispatch, rootState }, { campaignId, campaignType, customFilters, selectedList, smsContent, smsFormatting, includeSuppressed, confirmedTotalSegments, confirmedOverageAmount, filterSummary, recipientSource, removedPatNums, trackClicks, callFollowupTaskTypeID, priorityPreset, priorityConfig }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const siteID = rootState.customer.siteID;
      const country = localStorage.getItem('country') || 'USA';

      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}/send`, {
        siteID,
        campaignType: campaignType || 'Email',
        customFilters: customFilters || null,
        selectedList: selectedList || null,
        smsContent: smsContent || null,
        // 'optimized' (default) auto-replaces non-GSM chars server-side; 'original'
        // keeps them (customer accepted the extra Unicode segments in the composer)
        smsFormatting: smsFormatting || 'optimized',
        // Per-send override: include known dead/disconnected numbers this send only
        ...(includeSuppressed ? { includeSuppressed: true } : {}),
        // Present only on the resend after the user accepts the segment-count
        // confirmation dialog (server returns 409 + estimate on the first attempt)
        ...(confirmedTotalSegments != null ? { confirmedTotalSegments } : {}),
        // Present only on the resend after the user explicitly approves the
        // over-allowance charge (server 409s on any overage until this echoes the
        // quoted amount; re-priced server-side so a stale amount re-triggers).
        ...(confirmedOverageAmount != null ? { confirmedOverageAmount } : {}),
        filterSummary: filterSummary || null,
        recipientSource: recipientSource || null,
        removedPatNums: removedPatNums || [],
        trackClicks: trackClicks !== false,
        callFollowupTaskTypeID: callFollowupTaskTypeID || null,
        // AI Call ranking (additive): the backend applies these authoritatively at launch.
        // Only forwarded when present so non-ai_call sends are byte-for-byte unchanged.
        ...(priorityPreset ? { priorityPreset } : {}),
        ...(priorityConfig ? { priorityConfig } : {}),
        country
      });

      // Refresh the campaign to get updated status (will show 'sending')
      await dispatch('fetchCampaignById', campaignId);

      return response.data;
    } catch (error) {
      // These are handled gates, not failures — callers show the right dialog and
      // either resend or stop. Don't surface a generic error banner for them:
      //   409 + estimate     → segment/overage confirmation gate
      //   402 + paymentFailed → overage charge required
      //   422 + carrierBlocked → message too large for carriers (hard stop)
      const status = error.response?.status;
      const data = error.response?.data;
      const isHandledGate =
        (status === 409 && data?.estimate) ||
        (status === 402 && data?.paymentFailed) ||
        (status === 422 && data?.carrierBlocked);
      if (!isHandledGate) {
        console.error('Error sending campaign:', error);
        commit('SET_ERROR', extractApiError(error, 'Failed to send campaign'));
      }
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Month-to-date SMS usage for the pre-send breakdown in the composer:
   * how many texts the site has already used (conversations, reminders, ...)
   * against its monthly plan allowance. Returns { available:false } for sites
   * without a Twilio subaccount; null on error (UI simply hides the block).
   */
  async fetchSmsUsage({ rootState }) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/sms-usage`, {
        params: { siteID }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching SMS usage:', error);
      return null;
    }
  },

  /**
   * Start polling campaign status while status='sending'
   * Polls GET /campaigns/{id} every 5 seconds
   */
  startCampaignPolling({ commit, state, dispatch, rootState }, campaignId) {
    // Clear any existing polling
    if (state.pollingInterval) {
      clearInterval(state.pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        await dispatch('fetchCampaignById', campaignId);

        // Stop polling when campaign is no longer sending
        const campaign = state.currentCampaign;
        if (campaign && campaign.Status !== 'sending') {
          dispatch('stopCampaignPolling');
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000);

    commit('SET_POLLING_INTERVAL', interval);
  },

  /**
   * Stop polling campaign status
   */
  stopCampaignPolling({ commit, state }) {
    if (state.pollingInterval) {
      clearInterval(state.pollingInterval);
      commit('SET_POLLING_INTERVAL', null);
    }
  },

  /**
   * Retry failed recipients for a campaign
   */
  async retryCampaign({ commit, dispatch, rootState }, { campaignId, campaignType, smsContent }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const siteID = rootState.customer.siteID;

      // Re-send by hitting the same send endpoint — backend reads failed recipients
      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}/send`, {
        siteID,
        campaignType: campaignType || 'Email',
        retryFailed: true
      });

      await dispatch('fetchCampaignById', campaignId);
      return response.data;
    } catch (error) {
      console.error('Error retrying campaign:', error);
      commit('SET_ERROR', extractApiError(error, 'Failed to retry campaign'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Fetch recipients for a campaign
   */
  async fetchCampaignRecipients({ commit, rootState }, campaignId) {
    try {
      const siteID = rootState.customer.siteID;
      
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}/recipients`, {
        params: { siteID }
      });
      
      commit('SET_RECIPIENTS', response.data.recipients || []);
      return response.data.recipients;
    } catch (error) {
      console.error('Error fetching campaign recipients:', error);
      return [];
    }
  },
  
  async fetchCampaignClicks({ commit, rootState }, campaignId) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}/clicks`, {
        params: { siteID }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign clicks:', error);
      return { linkSummary: [], clickEvents: [] };
    }
  },

  /**
   * Fetch patient lists for recipient selection
   */
  async fetchPatientLists({ commit, rootState }) {
    try {
      const siteID = rootState.customer.siteID;
      
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/patient-lists`, {
        params: { siteID }
      });
      
      commit('SET_PATIENT_LISTS', response.data.lists || []);
      return response.data.lists;
    } catch (error) {
      console.error('Error fetching patient lists:', error);
      return [];
    }
  },
  
  /**
   * Preview recipients based on custom filter config
   */
  async previewRecipients({ rootState }, filterConfig) {
    try {
      const siteID = rootState.customer.siteID;

      // Handle "All Active Patients" recipient source
      if (filterConfig.recipientSource === 'all') {
        const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-preview-recipients`, {
          siteID,
          recipientSource: 'all',
          allActiveConfig: filterConfig.allActiveConfig
        });
        return response.data?.data || response.data || { count: 0, smsEligible: 0, emailEligible: 0 };
      }

      // Default: custom filters
      const { country, ...config } = filterConfig;
      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-preview-recipients`, {
        siteID,
        filterConfig: config,
        country: country || 'USA'
      });

      return response.data?.data || response.data || { count: 0, smsEligible: 0, emailEligible: 0 };
    } catch (error) {
      console.error('Error previewing recipients:', error);
      return { count: 0, smsEligible: 0, emailEligible: 0 };
    }
  },
  
  async previewScheduledPatients({ rootState }, { dateRange, providers }) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-preview-recipients`, {
        siteID,
        recipientSource: 'scheduledPatients',
        dateRange,
        providers: providers && providers.length > 0 ? providers : undefined
      });
      return response.data?.data || response.data || { count: 0, smsEligible: 0, emailEligible: 0, patients: [] };
    } catch (error) {
      console.error('Error previewing scheduled patients:', error);
      return { count: 0, smsEligible: 0, emailEligible: 0, patients: [] };
    }
  },

  /**
   * Fetch unique patient zip codes for demographic filter
   */
  async fetchPatientZipCodes({ rootState }) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-patient-zip-codes`, {
        params: { siteID }
      });
      return response.data?.data?.zipCodes || response.data?.zipCodes || [];
    } catch (error) {
      console.error('Error fetching patient zip codes:', error);
      return [];
    }
  },

  /**
   * Fetch insurance options (carriers, employers, group numbers) for campaign filters
   */
  async fetchInsuranceOptions({ rootState }) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-insurance-options`, {
        params: { siteID }
      });
      return response.data?.data || response.data || { carriers: [], employers: [], groupNumbers: [] };
    } catch (error) {
      console.error('Error fetching insurance options:', error);
      return { carriers: [], employers: [], groupNumbers: [] };
    }
  },

  /**
   * Fetch recall types (Open Dental `recalltype`) for the Recall Due filter dropdown.
   * Returns every type the practice has configured, matching the Overdue Recall page.
   */
  async fetchRecallTypes({ rootState }) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-recall-types`, {
        params: { siteID }
      });
      return response.data?.data?.recallTypes || response.data?.recallTypes || [];
    } catch (error) {
      console.error('Error fetching recall types:', error);
      return [];
    }
  },

  /**
   * Fetch SMS templates (wiki pages marked as SMS templates)
   */
  async fetchSmsTemplates({ commit, rootState }) {
    try {
      const siteID = rootState.customer.siteID;
      
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/wiki/templates`, {
        params: { siteID, type: 'sms' }
      });
      
      commit('SET_SMS_TEMPLATES', response.data.templates || []);
      return response.data.templates;
    } catch (error) {
      console.error('Error fetching SMS templates:', error);
      return [];
    }
  },
  
  /**
   * Fetch Email templates
   */
  async fetchEmailTemplates({ commit, rootState }) {
    try {
      const siteID = rootState.customer.siteID;
      
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/wiki/templates`, {
        params: { siteID, type: 'email' }
      });
      
      commit('SET_EMAIL_TEMPLATES', response.data.templates || []);
      return response.data.templates;
    } catch (error) {
      console.error('Error fetching email templates:', error);
      return [];
    }
  },
  
  /**
   * Export campaign report
   */
  async exportCampaignReport({ rootState }, { campaignId, format = 'csv' }) {
    try {
      const siteID = rootState.customer.siteID;
      
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}/export`, {
        params: { siteID, format },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaign-report-${campaignId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Error exporting campaign report:', error);
      throw error;
    }
  },
  
  /**
   * Clear current campaign selection
   */
  clearCurrentCampaign({ commit }) {
    commit('SET_CURRENT_CAMPAIGN', null);
    commit('SET_RECIPIENTS', []);
  },

  // ============================================
  // DUPLICATE & SAVED LISTS
  // ============================================

  /**
   * Duplicate an existing campaign
   */
  async duplicateCampaign({ commit, rootState }, campaignId) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}/duplicate`, {
        siteID
      });
      const newCampaign = response.data?.data || response.data;
      commit('ADD_CAMPAIGN', newCampaign);
      return newCampaign;
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      throw error;
    }
  },

  /**
   * Fetch saved patient lists from previous campaigns for reuse
   */
  async fetchSavedLists({ rootState }) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-saved-lists`, {
        params: { siteID }
      });
      return response.data?.data?.lists || response.data?.lists || [];
    } catch (error) {
      console.error('Error fetching saved lists:', error);
      return [];
    }
  },

  /**
   * Fetch Overdue Recall and Unscheduled Treatment lists for a site
   */
  async fetchSiteLists({ rootState }) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-site-lists`, {
        params: { siteID }
      });
      const data = response.data?.data || response.data || {};
      return {
        recallLists: data.recallLists || [],
        unscheduledLists: data.unscheduledLists || []
      };
    } catch (error) {
      console.error('Error fetching site lists:', error);
      return { recallLists: [], unscheduledLists: [] };
    }
  },

  /**
   * Fetch PatNums from a list's members (Overdue Recall or Unscheduled Treatment list)
   */
  async fetchListMembers({ rootState }, listID) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-list-members`, {
        params: { siteID, listID }
      });
      const data = response.data?.data || response.data || {};
      return {
        patNums: data.patNums || [],
        count: data.count || 0,
        patients: data.patients || [],
        smsEligible: data.smsEligible || 0,
        emailEligible: data.emailEligible || 0
      };
    } catch (error) {
      console.error('Error fetching list members:', error);
      return { patNums: [], count: 0 };
    }
  },

  async fetchPatientsByPatNums({ rootState }, patNums) {
    try {
      const siteID = rootState.customer.siteID;
      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-patients-by-patnums`, {
        siteID, patNums
      });
      const data = response.data?.data || response.data || {};
      return {
        patients: data.patients || [],
        count: data.count || 0,
        smsEligible: data.smsEligible || 0,
        emailEligible: data.emailEligible || 0
      };
    } catch (error) {
      console.error('Error fetching patients by PatNums:', error);
      return { patients: [], count: 0, smsEligible: 0, emailEligible: 0 };
    }
  },

  /**
   * Preview the RANKED AI-Call audience WITHOUT launching (Phase D.2b).
   * POST /campaigns/ai-call/preview-ranking — read-only, gated by AI_FOLLOWUP_PAID_FOR
   * (403 when not enabled, same gate as launch). Returns { ok, ranked:[...] }.
   * @returns {Promise<Array>} the ranked rows (or throws so the caller can surface the error)
   */
  async previewAiCallRanking({ rootState }, { selectedList, priorityPreset, priorityConfig }) {
    const siteID = rootState.customer.siteID;
    const response = await axios.post(
      `${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/ai-call/preview-ranking`,
      { siteID, selectedList: selectedList || [], priorityPreset, priorityConfig: priorityConfig || null }
    );
    const data = response.data || {};
    return Array.isArray(data.ranked) ? data.ranked : [];
  },

  // ============================================
  // NEW ACTIONS FOR ENHANCED FEATURES
  // ============================================

  /**
   * Archive a campaign
   */
  async archiveCampaign({ commit, rootState }, campaignId) {
    try {
      const siteID = rootState.customer.siteID;

      // In production, this would call the API
      // await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}/archive`, { siteID });

      // For now, just update local state
      commit('ARCHIVE_CAMPAIGN', campaignId);
      return true;
    } catch (error) {
      console.error('Error archiving campaign:', error);
      throw error;
    }
  },

  /**
   * Restore a campaign from archive
   */
  async restoreFromArchive({ commit, rootState }, campaignId) {
    try {
      const siteID = rootState.customer.siteID;

      // In production, this would call the API
      // await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}/restore`, { siteID });

      // For now, just update local state
      commit('RESTORE_CAMPAIGN', campaignId);
      return true;
    } catch (error) {
      console.error('Error restoring campaign:', error);
      throw error;
    }
  },

  /**
   * Send a test message for a campaign
   */
  async sendTestMessage({ rootState }, { campaignId, email, emailSubject, emailContent }) {
    try {
      const siteID = rootState.customer.siteID;

      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId || 'test'}/send-test`, {
        siteID,
        testEmail: email,
        emailSubject: emailSubject || null,
        emailContent: emailContent || null
      });

      return { success: true, message: response.data.message || 'Test message sent successfully' };
    } catch (error) {
      console.error('Error sending test message:', error);
      throw error;
    }
  },

  /**
   * Send a test SMS for a campaign
   */
  async sendTestSMS({ rootState }, { campaignId, testPhone, smsContent }) {
    try {
      const siteID = rootState.customer.siteID;

      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}/send-test-sms`, {
        siteID,
        testPhone,
        smsContent
      });

      return { success: true, message: response.data.message || 'Test SMS queued successfully' };
    } catch (error) {
      console.error('Error sending test SMS:', error);
      throw error;
    }
  },

  /**
   * Fetch email domain statistics for a campaign
   */
  async fetchDomainStats({ commit, rootState }, campaignId) {
    try {
      const siteID = rootState.customer.siteID;

      // In production, this would call the API
      // const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaigns/${campaignId}/domain-stats`, {
      //   params: { siteID }
      // });

      // For now, return mock data
      commit('SET_CAMPAIGN_DOMAIN_STATS', mockDomainStats);
      return mockDomainStats;
    } catch (error) {
      console.error('Error fetching domain stats:', error);
      return [];
    }
  },

  /**
   * Fetch global statistics across all campaigns
   */
  async fetchGlobalStats({ commit, rootState }, { startDate, endDate } = {}) {
    try {
      const siteID = rootState.customer.siteID;

      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-statistics`, {
        params: { siteID, startDate, endDate }
      });

      commit('SET_GLOBAL_STATS', response.data.stats || mockGlobalStats);
      commit('SET_STATS_TIMELINE', response.data.timeline || mockStatsTimeline);
      return { stats: response.data.stats, timeline: response.data.timeline };
    } catch (error) {
      console.error('Error fetching global stats:', error);
      return null;
    }
  },

  // ============================================
  // A/B TEST ACTIONS
  // ============================================

  /**
   * Fetch all A/B tests
   */
  async fetchAbTests({ commit, rootState }) {
    commit('SET_LOADING', true);
    try {
      const siteID = rootState.customer.siteID;

      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-ab-tests`, {
        params: { siteID }
      });

      commit('SET_AB_TESTS', response.data.tests || response.data || []);
      return response.data.tests || response.data;
    } catch (error) {
      console.error('Error fetching A/B tests:', error);
      commit('SET_ERROR', error.message);
      return [];
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Create a new A/B test
   */
  async createAbTest({ commit, rootState }, testData) {
    commit('SET_LOADING', true);
    try {
      const siteID = rootState.customer.siteID;

      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-ab-tests`, {
        ...testData,
        siteID: siteID
      });

      commit('ADD_AB_TEST', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating A/B test:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Delete an A/B test
   */
  async deleteAbTest({ commit, rootState }, testId) {
    try {
      const siteID = rootState.customer.siteID;

      await axios.delete(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-ab-tests/${testId}`, {
        params: { siteID }
      });

      commit('DELETE_AB_TEST', testId);
      return true;
    } catch (error) {
      console.error('Error deleting A/B test:', error);
      throw error;
    }
  },

  // ============================================
  // TEMPLATE ACTIONS
  // ============================================

  /**
   * Fetch campaign templates
   */
  async fetchCampaignTemplates({ commit, rootState }) {
    commit('SET_LOADING', true);
    try {
      const siteID = rootState.customer.siteID;

      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-templates`, {
        params: { siteID }
      });

      commit('SET_CAMPAIGN_TEMPLATES', response.data.templates || response.data || []);
      return response.data.templates || response.data;
    } catch (error) {
      console.error('Error fetching campaign templates:', error);
      return [];
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Create a new campaign template
   */
  async createCampaignTemplate({ commit, rootState }, templateData) {
    commit('SET_LOADING', true);
    try {
      const siteID = rootState.customer.siteID;

      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-templates`, {
        ...templateData,
        siteID: siteID
      });

      commit('ADD_CAMPAIGN_TEMPLATE', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating campaign template:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Update a campaign template
   */
  async updateCampaignTemplate({ commit, rootState }, { templateId, templateData }) {
    commit('SET_LOADING', true);
    try {
      const siteID = rootState.customer.siteID;

      const response = await axios.put(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-templates/${templateId}`, {
        ...templateData,
        siteID: siteID
      });

      commit('UPDATE_CAMPAIGN_TEMPLATE', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating campaign template:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Delete a campaign template
   */
  async deleteCampaignTemplate({ commit, rootState }, templateId) {
    try {
      const siteID = rootState.customer.siteID;

      await axios.delete(`${process.env.VUE_APP_TEAMIO_API_URL}/campaign-templates/${templateId}`, {
        params: { siteID }
      });

      commit('DELETE_CAMPAIGN_TEMPLATE', templateId);
      return true;
    } catch (error) {
      console.error('Error deleting campaign template:', error);
      throw error;
    }
  },

  /**
   * Duplicate a campaign template
   */
  async duplicateCampaignTemplate({ commit, state }, templateId) {
    const template = state.campaignTemplates.find(t => t.TemplateID === templateId);
    if (!template) return null;

    const newTemplate = {
      ...template,
      TemplateID: Date.now().toString(),
      Name: `${template.Name} (Copy)`,
      CreatedAt: new Date().toISOString(),
      UpdatedAt: new Date().toISOString(),
      UsageCount: 0
    };

    commit('ADD_CAMPAIGN_TEMPLATE', newTemplate);
    return newTemplate;
  },

  /**
   * Export template as HTML
   */
  async exportTemplateHtml({ state }, templateId) {
    const template = state.campaignTemplates.find(t => t.TemplateID === templateId);
    if (!template) return null;

    // Create download
    const blob = new Blob([template.Content || ''], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${template.Name.replace(/\s+/g, '-')}.html`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
